import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import ProductPopup from './ProductPopup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductCarousel = ({
  endpoint = '/products/featured/list',
  arabicTitle = '\u0639\u0637\u0648\u0631 \u0645\u062e\u062a\u0627\u0631\u0629',
  kicker = 'HANDPICKED FROM THE COLLECTION',
  titleMain = 'THE ROYAL',
  titleAccent = 'SELECTION',
  quote = '"Each bottle holds a story from the ancient trade routes..."',
  showNewBadge = false,
  sectionTestId = 'product-carousel-section',
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredId, setHoveredId] = useState(null);
  const [popup, setPopup] = useState(null);
  const carouselRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}${endpoint}`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [endpoint]);

  // Live stock polling — keeps SOLD OUT badges in sync with Shopify every 45s
  useEffect(() => {
    let cancelled = false;
    const syncStock = async () => {
      try {
        const { data: stockMap } = await axios.get(`${API}/products/stock-map`);
        if (cancelled) return;
        setProducts((prev) => prev.map((p) => (
          stockMap[p.id] !== undefined && stockMap[p.id] !== p.in_stock
            ? { ...p, in_stock: stockMap[p.id] }
            : p
        )));
      } catch (e) { /* ignore */ }
    };
    const interval = setInterval(syncStock, 45000);
    const onVisibility = () => { if (!document.hidden) syncStock(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const checkScrollButtons = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.addEventListener('scroll', checkScrollButtons, { passive: true });
      checkScrollButtons();
      return () => carousel.removeEventListener('scroll', checkScrollButtons);
    }
  }, [products]);

  const scroll = (direction) => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({
        left: direction === 'left' ? -400 : 400,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex gap-6 overflow-hidden">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex-shrink-0 w-[300px] md:w-[380px] h-[500px] bg-neutral-900/50 animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 md:py-32 bg-[#050505] relative overflow-hidden" data-testid={sectionTestId}>
      <div className="absolute inset-0 star-pattern opacity-20" />
      
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-14">
          <p className="font-arabic text-3xl text-[#8a7a60]/50 mb-2">{arabicTitle}</p>
          <p className="font-heading text-xs tracking-[0.3em] text-[#8a7a60] mb-4">{kicker}</p>
          <h2 className="font-heading text-4xl md:text-5xl tracking-wide text-white" data-testid="carousel-title">
            {titleMain} <span style={{ color: '#a89878' }}>{titleAccent}</span>
          </h2>
          <p className="font-body text-sm text-white/20 mt-3 italic max-w-md mx-auto">
            {quote}
          </p>
        </div>

        {/* Carousel with centered arrows */}
        <div className="relative">
          {/* Left Arrow */}
          <button
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            data-testid="carousel-prev-button"
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center transition-all duration-300"
            style={{
              background: canScrollLeft ? 'rgba(10,8,4,0.9)' : 'rgba(10,8,4,0.4)',
              border: `1px solid ${canScrollLeft ? '#4a3d2e' : '#2a231a'}`,
              color: canScrollLeft ? '#a89878' : '#3a3428',
              cursor: canScrollLeft ? 'pointer' : 'default',
              backdropFilter: 'blur(8px)',
            }}
          >
            <ChevronLeft size={24} />
          </button>

          {/* Right Arrow */}
          <button
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            data-testid="carousel-next-button"
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center transition-all duration-300"
            style={{
              background: canScrollRight ? 'rgba(10,8,4,0.9)' : 'rgba(10,8,4,0.4)',
              border: `1px solid ${canScrollRight ? '#4a3d2e' : '#2a231a'}`,
              color: canScrollRight ? '#a89878' : '#3a3428',
              cursor: canScrollRight ? 'pointer' : 'default',
              backdropFilter: 'blur(8px)',
            }}
          >
            <ChevronRight size={24} />
          </button>

          {/* Carousel scroll area */}
          <div
            ref={carouselRef}
            className="flex gap-6 overflow-x-auto hide-scrollbar scroll-smooth pb-4 px-8 md:px-16"
            data-testid="product-carousel"
          >
            {products.map((product) => (
              <div
                key={product.id}
                className="flex-shrink-0 w-[280px] md:w-[320px] cursor-pointer"
                onMouseEnter={() => setHoveredId(product.id)}
                onMouseLeave={() => setHoveredId(null)}
                onClick={() => setPopup(product)}
                data-testid={`product-card-${product.id}`}
              >
                <div className="group relative">
                  {/* Hover glow */}
                  <div className={`absolute -inset-6 transition-opacity duration-500 ${hoveredId === product.id ? 'opacity-100' : 'opacity-0'}`} style={{
                    background: 'radial-gradient(ellipse at center, rgba(80,70,50,0.2) 0%, transparent 60%)',
                    filter: 'blur(12px)'
                  }} />

                  {/* Ancient weathered frame */}
                  <div className="relative p-3" style={{
                    background: 'linear-gradient(135deg, #1a1510 0%, #2a2218 30%, #1f1a14 50%, #2a2218 70%, #1a1510 100%)',
                    boxShadow: 'inset 0 0 25px rgba(0,0,0,0.7), 0 8px 25px rgba(0,0,0,0.5), 0 0 15px rgba(255,255,255,0.06)',
                    filter: 'sepia(0.15) saturate(0.85)',
                  }}>
                    {/* Cracked aged texture */}
                    <div className="absolute inset-0 opacity-40 pointer-events-none" style={{
                      backgroundImage: `
                        linear-gradient(45deg, transparent 40%, rgba(60,50,35,0.4) 41%, transparent 42%),
                        linear-gradient(-60deg, transparent 55%, rgba(40,35,25,0.3) 56%, transparent 57%)
                      `,
                    }} />
                    {/* Worn carved borders */}
                    <div className="absolute inset-1 pointer-events-none" style={{ border: '3px solid #3d3428', boxShadow: 'inset 1px 1px 0 #4a3d2e, inset -1px -1px 0 #151210' }} />
                    <div className="absolute inset-2.5 pointer-events-none" style={{ border: '1px solid #2a231a' }} />
                    {/* Corner carvings */}
                    {['top-0 left-0', 'top-0 right-0 scale-x-[-1]', 'bottom-0 left-0 scale-y-[-1]', 'bottom-0 right-0 scale-[-1]'].map((pos, i) => (
                      <div key={i} className={`absolute ${pos} w-7 h-7 pointer-events-none`}>
                        <svg viewBox="0 0 28 28" className="w-full h-full opacity-50">
                          <path d="M0,0 L10,0 L10,3 L3,3 L3,10 L0,10 Z" fill="#4a3d2e" />
                          <circle cx="5" cy="5" r="1.5" fill="#2a231a" />
                        </svg>
                      </div>
                    ))}
                    {/* Top ornament */}
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 pointer-events-none">
                      <svg width="36" height="10" viewBox="0 0 36 10" className="opacity-45">
                        <rect x="0" y="4" width="12" height="2" fill="#3d3428" rx="1" />
                        <polygon points="18,0 23,5 18,10 13,5" fill="#4a3d2e" />
                        <polygon points="18,2 21,5 18,8 15,5" fill="#2a231a" />
                        <rect x="24" y="4" width="12" height="2" fill="#3d3428" rx="1" />
                      </svg>
                    </div>
                    {/* Bottom ornament */}
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 pointer-events-none">
                      <svg width="36" height="10" viewBox="0 0 36 10" className="opacity-45">
                        <rect x="0" y="4" width="12" height="2" fill="#3d3428" rx="1" />
                        <polygon points="18,0 23,5 18,10 13,5" fill="#4a3d2e" />
                        <polygon points="18,2 21,5 18,8 15,5" fill="#2a231a" />
                        <rect x="24" y="4" width="12" height="2" fill="#3d3428" rx="1" />
                      </svg>
                    </div>
                    {/* Image container */}
                    <div className="relative aspect-[3/4] overflow-hidden" style={{ background: '#080604', boxShadow: 'inset 0 0 40px rgba(0,0,0,0.9)' }}>
                      {product.discount_pct ? (
                        <div className="absolute top-3 left-3 z-10 px-2 py-1 font-heading text-[10px] tracking-[0.15em]" style={{ background: '#D4AF37', color: '#050505' }} data-testid={`carousel-discount-${product.id}`}>
                          {product.discount_pct}% OFF
                        </div>
                      ) : (showNewBadge && (
                        <div className="absolute top-3 left-3 z-10 px-2 py-1 font-heading text-[10px] tracking-[0.2em]" style={{ background: '#D4AF37', color: '#050505' }} data-testid={`carousel-new-badge-${product.id}`}>
                          NEW
                        </div>
                      ))}
                      {!product.in_stock && (
                        <div className="absolute top-3 right-3 z-10 px-2 py-1 font-heading text-[10px] tracking-[0.15em]" style={{ background: 'rgba(127,29,29,0.85)', color: '#fff', border: '1px solid rgba(239,68,68,0.4)' }}>
                          SOLD OUT
                        </div>
                      )}
                      {product.image ? (
                        <img src={product.image} alt={product.name} loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          style={{ filter: 'sepia(0.1) brightness(0.9)' }}
                          data-testid={`product-image-${product.id}`}
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center" data-testid={`product-image-${product.id}`}>
                          <span className="font-heading text-4xl mb-2" style={{ color: '#4a3d2e' }}>&#10022;</span>
                          <span className="font-heading text-[10px] tracking-[0.15em]" style={{ color: '#4a3d2e' }}>COMING SOON</span>
                        </div>
                      )}
                      <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, transparent 50%, rgba(60,50,35,0.4) 100%)' }} />
                      <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: 'inset 0 0 50px rgba(0,0,0,0.7)' }} />
                      {/* Tap to Discover overlay — always visible on mobile, hover on desktop */}
                      <div className="absolute inset-0 flex items-end justify-center pb-6 md:items-center md:pb-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300">
                        <span className="px-6 py-3 font-heading text-sm md:text-xs tracking-[0.2em]" style={{ background: 'rgba(10,8,4,0.9)', border: '1px solid rgba(255,255,255,0.3)', color: '#ffffff', textShadow: '0 0 8px rgba(255,255,255,0.3)' }}>
                          TAP TO DISCOVER
                        </span>
                      </div>
                    </div>
                    {/* Product Info */}
                    <div className="pt-4 pb-2 px-1">
                      <p className="font-heading text-[8px] tracking-[0.2em] mb-1" style={{ color: '#6a5a4a' }}>{product.brand?.toUpperCase()}</p>
                      <h3 className="font-heading text-base tracking-wide mb-0.5 transition-colors duration-200" style={{ color: hoveredId === product.id ? '#a89878' : '#c9b998' }} data-testid={`product-name-${product.id}`}>{product.name}</h3>
                      <p className="font-body text-sm mb-3" style={{ color: '#6a5a4a' }}>{product.subtitle}</p>
                      <div className="flex justify-between items-center pt-3" style={{ borderTop: '1px solid #2a231a' }}>
                        <div className="flex items-baseline gap-2">
                          {product.original_price && product.original_price > product.price && (
                            <span className="font-body text-xs line-through" style={{ color: '#5a4d3e' }}>A${product.original_price.toFixed(0)}</span>
                          )}
                          {product.price > 0 && (
                            <span className="font-heading text-lg" style={{ color: product.discount_pct ? '#D4AF37' : '#8a7a60' }} data-testid={`product-price-${product.id}`}>A${product.price.toFixed(2)}</span>
                          )}
                        </div>
                        <span className="font-body text-xs" style={{ color: '#4a3d2e' }}>{product.size}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* View All */}
        <div className="mt-12 text-center">
          <Link
            to="/products"
            data-testid="view-all-products-link"
            className="inline-flex items-center gap-3 px-8 py-4 font-heading text-xs tracking-[0.2em] transition-all duration-300 hover:bg-[#4a3d2e]/30"
            style={{ border: '2px solid #4a3d2e', color: '#a89878', background: 'linear-gradient(180deg, rgba(74,61,46,0.1) 0%, transparent 100%)' }}
          >
            <span style={{ color: '#6a5a4a' }}>&#10022;</span>
            VIEW ALL FRAGRANCES
            <span style={{ color: '#6a5a4a' }}>&#10022;</span>
          </Link>
        </div>
      </div>

      {/* Product Popup */}
      {popup && <ProductPopup product={popup} onClose={() => setPopup(null)} />}
    </section>
  );
};

export default ProductCarousel;
